import http from '../../shared/http'
import { IGroupSet, ITask, ITaskGroup } from '../../types/tasks.types'
import setsService from '../sets.service'
import groupsService from '../groups.service'
import tasksService from '../tasks/tasks.service'
import { IFullPackResponse, IPack } from './packs.types'
import { ISandboxState } from '../../screens/Sandbox/sandbox.types'
import levelsService from '../levels.service'

const getPacks = async (): Promise<IPack[]> => {
  return http.get('/api/v1/admin/exercise_packs/').then((r) => r.data.results)
}

const getFullPack = async (packId: IPack['id']): Promise<IFullPackResponse> => {
  const sets: IGroupSet[] = await setsService.getPackSets(packId)
  const groups: ITaskGroup[] = await groupsService.getSetsGroups(sets.map((s) => s.id))
  const tasks = await tasksService.getGroupsTasksWithSetIds(sets.map((s) => s.id))
  const levels = await levelsService.getList(packId)

  return {
    levels,
    sets,
    groups: groups.sort((a, b) => a.orderingIndex - b.orderingIndex),
    tasks,
  }
}

const processFullPackResponseIntoSandboxState = (response: IFullPackResponse): ISandboxState['tasks'] => {
  const state = {}
  const setToGroupsMap = new Map<IGroupSet['id'], ITaskGroup[]>()
  const groupToTasksMap = new Map<ITaskGroup['id'], ITask[]>()

  for (const group of response.groups) {
    if (setToGroupsMap.has(group.set)) setToGroupsMap.get(group.set).push(group)
    else setToGroupsMap.set(group.set, [group])
  }

  for (const task of response.tasks) {
    if (groupToTasksMap.has(task.group)) groupToTasksMap.get(task.group).push(task)
    else groupToTasksMap.set(task.group, [task])
  }

  for (const set of response.sets) {
    const groups = setToGroupsMap.get(set.id)
    for (const group of groups) {
      const tasks = groupToTasksMap.get(group.id)
      for (const task of tasks) {
        state[`${set.id}$${group.id}$${task.id}`] = task
      }
    }
  }

  return state as any
}

const createPack = async (pack: Pick<IPack, 'version' | 'name'>): Promise<IPack> => {
  const created: IPack = await http.post('/api/v1/admin/exercise_packs/', pack).then((r) => r.data)

  // init data
  // Create 3 sets (set for each rocket)
  const createSetRequests = arr(3).map(() => setsService.createSet(created.id))
  const sets = await Promise.all(createSetRequests)

  // Create 30 groups (10 stars for each set)
  const createGroupRequests = sets.flatMap((s, ind) => arr(10).map(() => groupsService.createGroup(s.id, ind > 0)))
  const groups = await Promise.all(createGroupRequests)

  // Create 300 tasks (10 tasks for each group)
  const createTaskRequests = groups.flatMap((g) => arr(10).map(() => tasksService.createEmptyTask(g.id)))
  await Promise.all(createTaskRequests)

  return created
}

const createRocket = async (packId: IPack['id'], level = undefined): Promise<IFullPackResponse> => {
  const set = await setsService.createSet(packId, level)

  const groupsRequests = arr(10).map(() => groupsService.createGroup(set.id, true))
  const groups = await Promise.all(groupsRequests)

  const createTaskRequests = groups.flatMap((g) => arr(10).map(() => tasksService.createEmptyTask(g.id)))
  const tasks = await Promise.all(createTaskRequests)

  const levels = await levelsService.getList(packId)

  return { levels, sets: [set], groups, tasks }
}

const deployPack = (pack: IPack['id']) => {
  return http.post(`/api/v1/admin/exercise_packs/${pack}/deploy/`)
}

const arr = (size: number) => new Array(size).fill('')

const packsService = {
  getPacks,
  createPack,
  getFullPack,
  processFullPackResponseIntoSandboxState,
  deployPack,
  createRocket,
}
export default packsService
